<template>
    <LazyHydration :hydrate="hydrateOnIdle()">
        <HeadMetaTwoPage
            :title="headTitle"
            :description="headDescription"
            :image="game?.preview"
        />
    </LazyHydration>
    <div
        v-if="game"
        :class="$style.page"
    >
        <iframe
            :src="exportUrl"
            :allow="iframeAllowAttribute"
            :class="$style.frame"
            :title="game.title"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, hydrateOnIdle, onMounted, onServerPrefetch, toRef } from 'vue'
import { useRoute } from 'vue-router'

import LazyHydration from '@/components_new/LazyHydration.vue'
import { useGetExternalUrl } from '@/composables/url-helpers'
import { AUTOPLAY_PARAM } from '@/router/query-params'
import { useAppStore } from '@/store/app-store'

import { useGamePageData } from '../Games/use-game-page-data'
import { iframeAllowAttribute } from '../Games/utils'
import HeadMetaTwoPage from './HeadMetaTwoPage.vue'
import { TWO_PAGE_PARTNER, buildTwoPageExportRoute, getTwoPageHruBySlug } from './two-page-data'

const route = useRoute()
const appStore = useAppStore()
const getExternalUrl = useGetExternalUrl()

const hru = computed(() => getTwoPageHruBySlug(route.params.gameSlug as string) || '')
const hruRef = toRef(() => hru.value)
const partnerClid = computed(() => appStore.getWhiteLabelClid(TWO_PAGE_PARTNER))

const { game, preloadGameData, processGameResponse } = useGamePageData({
    hru: hruRef,
    isExport: false,
})

const exportUrl = computed(() => {
    if (!hru.value) {
        return ''
    }

    return getExternalUrl(
        buildTwoPageExportRoute(hru.value, partnerClid.value, {
            ...route.query,
            [AUTOPLAY_PARAM]: 'true',
        }),
    )
})

const headTitle = computed(() => game.value?.title || 'Loading game')
const headDescription = computed(() => game.value?.description || 'Loading game')

function processResponse() {
    if (!hru.value) {
        appStore.setPageStatus(404)
        // eslint-disable-next-line no-throw-literal
        throw 404
    }

    processGameResponse()
}

onServerPrefetch(async () => {
    if (hru.value) {
        await preloadGameData()
    }

    processResponse()
})

onMounted(async () => {
    if (hru.value) {
        await preloadGameData()
    }

    processResponse()
})
</script>

<style module>
.page {
    min-height: 100svh;
    background: #05070d;
    display: flex;
}

.frame {
    width: 100%;
    height: 100svh;
    border: 0;
    display: block;
}
</style>
